import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Share2 } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { RelatedBlogPosts } from '../../../../components/Blog/RelatedBlogPosts';
import GoogleTranslateWidget from '../../../../components/GoogleTranslateWidget';
export function BlogPostNarcissismGaslightingPT() {
  const articleData = {
    title: 'Aprenda Sobre o Termo: Narcisismo e Gaslighting',
    description: 'Confuso com comportamentos narcisistas ou gaslighting? Aprenda os principais sinais em textos e veja como a IA do Toxic Detector identifica instantaneamente padrões de manipulação sutis.',
    slug: 'narcissism-gaslighting-explained',
    publishDate: '2025-07-15',
    author: 'Toxic Detector Editorial'
  };
  const handleShare = async () => {
    try {
      await navigator.share({
        title: articleData.title,
        text: articleData.description,
        url: window.location.href
      });
    } catch (err) {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copiado para a área de transferência!');
    }
  };
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: articleData.title,
    description: articleData.description,
    image: 'https://toxicdetector.com/pt/blog/narcissism-gaslighting-explained.jpg',
    author: {
      '@type': 'Person',
      name: articleData.author
    },
    publisher: {
      '@type': 'Organization',
      name: 'ToxicDetector',
      logo: {
        '@type': 'ImageObject',
        url: 'https://toxicdetector.com/logo.png'
      }
    },
    datePublished: articleData.publishDate,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://toxicdetector.com/pt/blog/${articleData.slug}`
    }
  };
  return <div className="min-h-screen bg-gray-50" data-id="element-9087">
      <GoogleTranslateWidget data-id="element-9088" />
      <Helmet data-id="element-9089">
        <title data-id="element-9090">Narcisismo e Gaslighting Explicados | ToxicDetector</title>
        <meta name="description" content="Entenda a psicologia por trás das estratégias de manipulação de narcisismo e gaslighting." data-id="element-9091" />
        <meta property="og:image" content="https://cdn.magicpatterns.com/uploads/iLMUMmEFFWiRSqv8DSTAdz/file_00000000f0c8720a9c4e7f9e2fbdea6d.png" data-id="element-9092" />
        <meta name="keywords" content="narcisismo, gaslighting, manipulação, psicologia, análise de IA" data-id="element-9093" />
        <link rel="canonical" href={`https://toxicdetector.com/pt/blog/${articleData.slug}`} data-id="element-9094" />
        <script type="application/ld+json" data-id="element-9095">
          {JSON.stringify(articleSchema)}
        </script>
      </Helmet>

      <header className="bg-white/90 border-b border-gray-200 sticky top-0 z-50 backdrop-blur-sm" data-id="element-9096">
        <div className="max-w-3xl mx-auto px-6 py-4" data-id="element-9097">
          <div className="flex items-center justify-between" data-id="element-9098">
            <Link to="/" className="text-2xl font-bold tracking-tight text-gray-900" data-id="element-9099">
              🚩 ToxicDetector
            </Link>
            <button onClick={handleShare} className="p-2.5 hover:bg-gray-50 rounded-lg transition-colors" aria-label="Share article" data-id="element-9100">
              <Share2 size={20} className="text-gray-600" data-id="element-9101" />
            </button>
          </div>
        </div>
      </header>

      <article className="max-w-3xl mx-auto w-full px-6 py-12" data-id="element-9102">
        <Link to="/blog" className="inline-flex items-center text-gray-600 text-sm mb-10 hover:text-gray-900 transition-colors group" data-id="element-9103">
          <ArrowLeft size={16} className="mr-2 group-hover:-translate-x-1 transition-transform" data-id="element-9104" />
          Voltar ao Blog
        </Link>

        <header className="mb-12" data-id="element-9105">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight leading-tight text-gray-900" data-id="element-9106">
            {articleData.title}
          </h1>
          <div className="flex items-center text-gray-600 text-sm gap-3" data-id="element-9107">
            <time dateTime={articleData.publishDate} data-id="element-9108">
              {new Date(articleData.publishDate).toLocaleDateString('pt-BR', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
            </time>
            <span className="text-gray-300" data-id="element-9109">•</span>
            <span data-id="element-9110">{articleData.author}</span>
          </div>
        </header>

        <div className="prose prose-lg max-w-none" data-id="element-9111">
          <p className="text-xl mb-8 leading-relaxed text-gray-700" data-id="element-9112">
            Já se perguntou por que algumas pessoas fazem você se sentir
            confuso, cansado ou duvidando de si mesmo em relacionamentos? Isso
            geralmente é <strong data-id="element-9113">narcisismo e gaslighting</strong> em ação.
          </p>

          <section className="mb-12" data-id="element-9114">
            <h2 className="text-3xl font-bold mb-6 tracking-tight text-gray-900" data-id="element-9115">
              O que é Narcisismo?
            </h2>
            <p className="text-lg leading-relaxed text-gray-600 mb-6" data-id="element-9116">
              <strong data-id="element-9117">Narcisismo</strong> é um padrão de comportamento
              egocêntrico e manipulador onde alguém prioriza suas próprias
              necessidades sobre as dos outros.
            </p>
          </section>

          <section className="mb-12" data-id="element-9118">
            <h2 className="text-3xl font-bold mb-6 tracking-tight text-gray-900" data-id="element-9119">
              O que é Gaslighting?
            </h2>
            <p className="text-lg leading-relaxed text-gray-600 mb-6" data-id="element-9120">
              <strong data-id="element-9121">Gaslighting</strong> é uma técnica de manipulação
              psicológica que faz você questionar sua própria realidade, memória
              ou sentimentos.
            </p>
          </section>

          <section className="mb-12" data-id="element-9122">
            <h2 className="text-3xl font-bold mb-6 tracking-tight text-gray-900" data-id="element-9123">
              Como Nossa IA Detecta Esses Padrões
            </h2>
            <p className="text-lg leading-relaxed text-gray-600 mb-6" data-id="element-9124">
              Em vez de adivinhar, nossa IA analisa suas conversas em busca de
              padrões de manipulação.
            </p>
          </section>

          <section className="mb-12" data-id="element-9125">
            <h2 className="text-3xl font-bold mb-6 tracking-tight text-gray-900" data-id="element-9126">
              Por Que Entender Isso Importa
            </h2>
            <p className="text-lg leading-relaxed text-gray-600" data-id="element-9127">
              Reconhecer <strong data-id="element-9128">narcisismo</strong> ou{' '}
              <strong data-id="element-9129">gaslighting</strong> cedo protege sua saúde emocional.
            </p>
          </section>
        </div>

        <div className="bg-purple-600 rounded-2xl p-10 text-center text-white mt-16" data-id="element-9130">
          <h2 className="text-3xl font-bold mb-4 tracking-tight" data-id="element-9131">
            💀 Pronto Para Ver a Verdade?
          </h2>
          <p className="text-purple-100 mb-8 text-lg leading-relaxed max-w-2xl mx-auto" data-id="element-9132">
            Pare de adivinhar. Envie seus textos para o Toxic Detector e obtenha
            uma pontuação de toxicidade instantânea.
          </p>
          <Link to="/signup" className="inline-block bg-white text-purple-600 px-10 py-4 rounded-lg font-bold text-lg hover:bg-gray-50 transition-colors" data-id="element-9133">
            Escaneie Seus Chats Agora →
          </Link>
        </div>

        <div className="mt-16" data-id="element-9134">
          <RelatedBlogPosts posts={[{
          title: '7 Sinais de Gaslighting',
          description: 'Aprenda os principais padrões de gaslighting que a IA detecta em suas conversas de texto.',
          slug: '7-signs-of-gaslighting',
          emoji: '💀'
        }, {
          title: 'Seu Ex é um Narcisista Oculto?',
          description: 'Descubra se seu ex mostra sinais de comportamento narcisista oculto através da análise de texto.',
          slug: 'is-your-ex-a-covert-narcissist',
          emoji: '🎭'
        }]} data-id="element-9135" />
        </div>
      </article>

      <footer className="bg-white border-t border-gray-200 py-12 mt-16" data-id="element-9136">
        <div className="max-w-3xl mx-auto px-6 text-center" data-id="element-9137">
          <p className="text-gray-500 text-sm" data-id="element-9138">
            © {new Date().getFullYear()} ToxicDetector. Todos os direitos
            reservados.
          </p>
        </div>
      </footer>
    </div>;
}